import { useMemo } from 'react'
import * as React from 'react'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import MultiRoomBedbankMultiCartReviewGroup from './MultiRoomBedbankMultiCartReviewGroup'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import useUserMultiCartBedbankHotelItemData from '../hooks/useUserMultiCartBedbankHotelItemData'
import { queryKeyAdults, queryKeyCheckIn, queryKeyCheckOut, queryKeyChildren } from 'constants/url'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { useSelector } from '@xstate/react'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { dateIsBeforeOrSame } from 'lib/datetime/dateUtils'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'

interface Props {
  multiCartItems: Array<App.MultiCartBedbankHotelItem>
  tileVariant?: 'default' | 'condensed'
}

function BedbankMultiCartReviewGroup({ multiCartItems, tileVariant = 'default' }: Props) {
  // ============================================================================
  // HOOKS AND CONTEXT
  // ============================================================================
  const referenceMultiCartItem = multiCartItems[0]
  const multiCartActorRef = MultiCartContext.useActorRef()
  const referenceData = useUserMultiCartBedbankHotelItemData(referenceMultiCartItem, false)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  // State selectors
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartItemsState = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems.status)

  // ============================================================================
  // STATE CALCULATIONS
  // ============================================================================
  const areAllItemsSavedForLater = multiCartItems.length > 0 && multiCartItems.every(item => item.state === 'save_for_later')
  const areAllItemsDeleted = multiCartItems.length > 0 && multiCartItems.every(item => item.state === 'deleted')
  const isAnyItemDeleted = multiCartItems.length > 0 && multiCartItems.some(item => item.state === 'deleted')
  const isAnyItemOnPastDates = multiCartItems.some(item => dateIsBeforeOrSame(new Date(item.checkIn), new Date()))

  // For bedbank items, we check if they're sold out based on the reference data
  const isAnyItemSoldOut = useMemo(() => {
    return referenceData.isSoldOut || !referenceData.isBookable
  }, [referenceData.isSoldOut, referenceData.isBookable])

  const isAnyItemUnavailable = useMemo(() => {
    // For bedbank, we consider items unavailable if there's an error or if they're not bookable
    return !!referenceData.error || !referenceData.isBookable
  }, [referenceData.error, referenceData.isBookable])

  // ============================================================================
  // COMPUTED VALUES AND MEMOIZED DATA
  // ============================================================================
  const { hotelName, publicTotalPrice } = useMemo(() => {
    let publicTotalPrice: number | undefined

    if (referenceData.itemTotals) {
      // Calculate total for all non-deleted items
      const totalActiveItems = multiCartItems.filter(item => item.state !== 'deleted').length
      if (totalActiveItems > 0) {
        // For now, we'll use the reference item totals multiplied by the number of active items
        // This is a simplified approach - in a real scenario, you'd want to calculate each item individually
        const { publicTotal } = getUserCartItemDisplayTotals(referenceData.itemTotals)
        publicTotalPrice = publicTotal * totalActiveItems
      }
    }

    return {
      hotelName: referenceData.offer?.property.name || referenceData.offer?.name || '',
      publicTotalPrice,
    }
  }, [referenceData, multiCartItems])

  const offerLink = useMemo(() => {
    const params = new URLSearchParams()
    const rooms = multiCartItems.map(i => i.occupancy)

    if (!referenceData.isPast) {
      params.set(queryKeyCheckIn, referenceMultiCartItem.checkIn)
      params.set(queryKeyCheckOut, referenceMultiCartItem.checkOut)
    }

    params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    return offerPageURL(referenceData.offer, params)
  }, [referenceData.offer, referenceMultiCartItem, referenceData.isPast, multiCartItems])

  // ============================================================================
  // UI STATE CALCULATIONS
  // ============================================================================
  const groupDisabled = isAnyItemUnavailable || isAnyItemSoldOut || isAnyItemOnPastDates
  const bannerTypeVariant = getBannerTypeVariant(isAnyItemOnPastDates, isAnyItemSoldOut, isAnyItemUnavailable, !!referenceData.error)
  const fetching = cartItemsState === 'loading' && referenceData.loading && !referenceData.loaded

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const handleSaveAllForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!activeCartId) return

    multiCartReviewActorRef.send({
      type: 'update save items for later',
      cartId: activeCartId,
      itemIds: multiCartItems.map(item => item.id),
      saveForLater: !areAllItemsSavedForLater,
    })
  }

  const handleDeleteAll = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!areAllItemsDeleted && activeCartId) {
      multiCartActorRef.send({
        type: 'delete items from given cart',
        cartId: activeCartId,
        itemIds: multiCartItems.map(item => item.id),
        currentStates: multiCartItems.reduce((acc, item) => {
          acc[item.id] = item.state
          return acc
        }, {} as Record<string, App.MultiCartItemState>),
      })
    }
  }

  // ============================================================================
  // EARLY RETURNS
  // ============================================================================
  if (areAllItemsDeleted) {
    return <UndoDeletedItemsTile
      title={hotelName}
      onUndoClick={() => {
        multiCartActorRef.send({
          type: 'undo delete',
          itemIds: multiCartItems.map(item => item.id),
        })
      }}
    />
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <MultiRoomBedbankMultiCartReviewGroup
      multiCartItems={multiCartItems}
      banner={fetching ? null : <CartReviewBanner variant={bannerTypeVariant} to={offerLink} />}
      showOverlay={areAllItemsSavedForLater || groupDisabled}
      hotelName={hotelName}
      checkIn={referenceMultiCartItem.checkIn}
      checkOut={referenceMultiCartItem.checkOut}
      duration={referenceMultiCartItem.duration}
      headerPrice={!!publicTotalPrice && <PriceRowPrice size="S" price={publicTotalPrice} saleUnit="total" signDisplay="never" priceFormat="preciseDollar" />}
      onSaveAllForLater={handleSaveAllForLater}
      onDeleteAll={handleDeleteAll}
      areAllItemsSavedForLater={areAllItemsSavedForLater}
      areAllItemsDeleted={areAllItemsDeleted}
      isAnyItemDeleted={isAnyItemDeleted}
      fetching={fetching}
      groupDisabled={groupDisabled}
      tileVariant={tileVariant}
    />
  )
}

export default BedbankMultiCartReviewGroup
